/** @format */

import { type ComponentType, type LazyExoticComponent, lazy } from "react";

/**
 * Error patterns that indicate a stale chunk (module not found after deployment).
 * These patterns match errors thrown by browsers when dynamic imports fail.
 */
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /Loading chunk \d+ failed/,
  /Loading CSS chunk \d+ failed/,
  /Unable to preload CSS/,
  /Importing a module script failed/,
  /error loading dynamically imported module/i,
];

/**
 * Check if an error is a chunk loading error (stale deployment).
 * This detects when a user's cached index.html references chunks that
 * no longer exist after a new deployment.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(error.message));
}

/**
 * Storage key to track refresh attempts and prevent infinite loops.
 */
const CHUNK_REFRESH_KEY = "galaxie_chunk_refresh";
const REFRESH_WINDOW_MS = 10_000; // 10 seconds

/**
 * Check if we recently refreshed due to chunk error (prevent loops).
 */
function hasRecentlyRefreshed(): boolean {
  try {
    const lastRefresh = sessionStorage.getItem(CHUNK_REFRESH_KEY);
    if (!lastRefresh) {
      return false;
    }
    const timestamp = parseInt(lastRefresh, 10);
    return Date.now() - timestamp < REFRESH_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * Mark that we're about to refresh due to chunk error.
 */
function markRefresh(): void {
  try {
    sessionStorage.setItem(CHUNK_REFRESH_KEY, Date.now().toString());
  } catch {
    // Ignore storage errors (e.g., private browsing mode)
  }
}

/**
 * Clear the refresh marker. Call on successful page load.
 */
export function clearRefreshMarker(): void {
  try {
    sessionStorage.removeItem(CHUNK_REFRESH_KEY);
  } catch {
    // Ignore storage errors
  }
}

interface LazyWithRetryOptions {
  /** Number of retry attempts before giving up (default: 2) */
  retries?: number;
  /** Base delay in ms for exponential backoff (default: 1000) */
  baseDelay?: number;
  /** Whether to auto-refresh on final failure (default: true) */
  autoRefresh?: boolean;
}

/**
 * Wrapper around React.lazy that handles stale chunk errors with retry logic.
 *
 * On chunk load failure:
 * 1. Retries with exponential backoff (1s, 2s delays)
 * 2. If retries exhausted and autoRefresh enabled, triggers page refresh
 * 3. Uses sessionStorage to prevent infinite refresh loops
 *
 * @example
 * // Instead of:
 * const MyComponent = lazy(() => import("./MyComponent"));
 *
 * // Use:
 * const MyComponent = lazyWithRetry(() => import("./MyComponent"));
 */
// biome-ignore lint/suspicious/noExplicitAny: Required for compatibility with various component prop types
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: LazyWithRetryOptions = {}
): LazyExoticComponent<T> {
  const { retries = 2, baseDelay = 1000, autoRefresh = true } = options;

  return lazy(async () => {
    let lastError: Error = new Error("Unknown chunk load error");

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Add delay before retries (not on first attempt)
        if (attempt > 0) {
          await new Promise(resolve =>
            setTimeout(resolve, baseDelay * 2 ** (attempt - 1))
          );
        }

        const module = await importFn();
        // Success - clear any refresh marker
        clearRefreshMarker();
        return module;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (!isChunkLoadError(lastError)) {
          // Not a chunk error - throw immediately without retry
          throw lastError;
        }

        // Log retry attempt for debugging
        console.warn(
          `[lazyWithRetry] Chunk load failed (attempt ${attempt + 1}/${retries + 1}):`,
          lastError.message
        );
      }
    }

    // All retries exhausted
    if (autoRefresh && !hasRecentlyRefreshed()) {
      console.warn("[lazyWithRetry] Retries exhausted, refreshing page...");
      markRefresh();
      window.location.reload();
      // Return a promise that never resolves (page is reloading)
      return new Promise(() => {});
    }

    // Either autoRefresh is disabled or we recently refreshed
    // Throw the error to be caught by ErrorBoundary
    throw lastError;
  });
}

export default lazyWithRetry;
